import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import MuiButton from "../../components/basicComponent/MuiButton";
import MuiDatePicker from "../../components/basicComponent/MuiDatePicker";
import { MuiInput } from "../../components/basicComponent/MuiInput";
import MuiSearchSelect from "../../components/basicComponent/MuiSearchSelect";
import { msgTypes } from "../../constants/msgTypes";
import useCompanyList from "../../services/customHooks/useCompanyList";
import { UtilService } from "../../services/shared/UtilService";
import Header from "../../sharedScreens/Header";
import SideMenu from "../../sharedScreens/menuItems/SideMenu";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import Box from "@mui/material/Box";
import Popper from "@mui/material/Popper";
import Fade from "@mui/material/Fade";
import ExclusiveOffersPreview from "../../components/composite/hotelListCard/hotelMainCard/ExclusiveOffersPreview";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";

import * as Yup from "yup";
import { hotelConfig } from "../../config/hotelConfig";

dayjs.extend(isSameOrAfter);

interface ChildInfo {
  NumberOfChild: number;
  childAge: number[];
}

interface RoomInfo {
  NumberOfAdults: number;
  Child: ChildInfo;
}

interface RoomsData {
  Count: string;
  Room: RoomInfo[];
}

const HotelBooking: React.FC = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [citySearch, setCitySearch] = useState("");
  const [city, setCity] = useState([]);
  const [nationality, setNationality] = useState([]);
  const [ratingAnchorEl, setRatingAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [isRatingPopperOpen, setIsRatingPopperOpen] = useState(false);
  const [selectedStars, setSelectedStars] = useState<number[]>([]);
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: {
      city: "",
      country: "",
      CheckInDate: dayjs().format("YYYY-MM-DD"),
      CheckOutDate: dayjs().add(1, "day").format("YYYY-MM-DD"),
      nationality: "",
      residentCountry: "",
      nights: 1,
      rooms: {
        Count: "1 Room, 2 Guests",
        Room: [
          {
            NumberOfAdults: 2,
            Child: { NumberOfChild: 0, childAge: [] },
          },
        ],
      },
    },
    validationSchema: Yup.object({
      city: Yup.string().required("City is required"),
      CheckInDate: Yup.string().required("Check-in date is required"),
      CheckOutDate: Yup.string()
        .required("Check-out date is required")
        .test(
          "is-after-checkin",
          "Check-out date must be after check-in",
          function (value) {
            return dayjs(value).isAfter(dayjs(this.parent.CheckInDate));
          }
        ),
    }),
    onSubmit: (values) => {
      const payload = {
        Chain: 0,
        CheckInDate: values.CheckInDate,
        CheckOutDate: values.CheckOutDate,
        City: values.city,
        CityName: values.city,

        IsEMTAPI: true,
        Nights: values.nights,
        country: values.country,
        nationality: values.nationality,
        rooms: {
          Count: values.rooms.Room.length,
          Room: values.rooms.Room,
        },
        username: hotelConfig.hotelUsername,
        selectedStars,
      };
      navigate("/hotellist", { state: { payload } });
    },
  });

  useEffect(() => {
    if (citySearch.length >= 3 && citySearch !== formik.values.city) {
      const delayDebounce = setTimeout(async () => {
        const response = await UtilService.fetchCities(citySearch);
        if (response?.statusMsg === msgTypes.SUCCESS.msg) {
          const formattedCities = response.data.map((item: any) => ({
            label: `${item.city}, ${item.country}`,
            value: `${item.city}, ${item.country}`,
          }));
          setCity(formattedCities);
        }
      }, 500);
      return () => clearTimeout(delayDebounce);
    }
  }, [citySearch, formik.values.city]);

  useEffect(() => {
    const getCountryCode = async () => {
      try {
        const res = await UtilService.getCountryCode();
        if (res.statusMsg === msgTypes.SUCCESS.msg) {
          const nationalityOptions = res.data.map(
            (i: { nationality: string }) => ({
              label: i.nationality,
              value: i.nationality,
            })
          );
          setNationality(nationalityOptions);
        }
      } catch (error) {
        console.error("Error while fetching nationality:", error);
      }
    };
    getCountryCode();
  }, []);

  const handleRatingClick = (event: React.MouseEvent<HTMLElement>) => {
    setRatingAnchorEl(event.currentTarget);
    setIsRatingPopperOpen((prev) => !prev);
  };

  const toggleStarSelection = (star: number) => {
    setSelectedStars((prevStars) =>
      prevStars.includes(star)
        ? prevStars.filter((s) => s !== star)
        : [...prevStars, star]
    );
  };
  const SelectTravellers = () => {
    const handleIncrement = (
      type: "adults" | "children",
      roomIndex: number
    ) => {
      const room = formik.values.rooms.Room[roomIndex];
      const total = room.NumberOfAdults + room.Child.NumberOfChild;

      if (type === "adults" && room.NumberOfAdults < 12 && total < 12) {
        formik.setFieldValue(
          `rooms.Room[${roomIndex}].NumberOfAdults`,
          room.NumberOfAdults + 1
        );
      }

      if (type === "children" && room.Child.NumberOfChild < 12 && total < 12) {
        formik.setFieldValue(
          `rooms.Room[${roomIndex}].Child.NumberOfChild`,
          room.Child.NumberOfChild + 1
        );
      }
    };

    const handleDecrement = (
      type: "adults" | "children",
      roomIndex: number
    ) => {
      const room = formik.values.rooms.Room[roomIndex];
      if (type === "adults" && room.NumberOfAdults > 1) {
        formik.setFieldValue(
          `rooms.Room[${roomIndex}].NumberOfAdults`,
          room.NumberOfAdults - 1
        );
      }
      if (type === "children" && room.Child.NumberOfChild > 0) {
        formik.setFieldValue(
          `rooms.Room[${roomIndex}].Child.NumberOfChild`,
          room.Child.NumberOfChild - 1
        );
      }
    };

    const handleAddRoom = () => {
      const current = formik.values.rooms.Room;
      if (current.length < 12) {
        const newRoom: RoomInfo = {
          NumberOfAdults: 1,
          Child: {
            NumberOfChild: 0,
            childAge: [],
          },
        };
        formik.setFieldValue("rooms.Room", [...current, newRoom]);
      }
    };

    const handleRemoveRoom = (roomIndex: number) => {
      const updatedRooms = [...formik.values.rooms.Room];
      updatedRooms.splice(roomIndex, 1);
      formik.setFieldValue("rooms.Room", updatedRooms);
    };

    const handleDone = () => {
      const rooms = formik.values.rooms.Room;
      const totalAdults = rooms.reduce(
        (sum, room) => sum + room.NumberOfAdults,
        0
      );
      const totalChildren = rooms.reduce(
        (sum, room) => sum + room.Child.NumberOfChild,
        0
      );
      const totalGuests = totalAdults + totalChildren;
      const roomCount = rooms.length;

      const summary = `${roomCount} Room${
        roomCount > 1 ? "s" : ""
      }, ${totalGuests} Guest${totalGuests !== 1 ? "s" : ""}`;
      formik.setFieldValue("rooms.Count", summary);
      setIsPopupOpen(false);
    };

    return (
      <div className="select-travellers-container bg-white p-3 rounded shadow absolute">
        <h3 className="text-lg mb-1 font-semibold">
          {formik.values.rooms.Room.length} Room
          {formik.values.rooms.Room.length > 1 ? "s" : ""},{" "}
          {formik.values.rooms.Room.reduce(
            (total, room) =>
              total + room.NumberOfAdults + room.Child.NumberOfChild,
            0
          )}
          Guests
        </h3>

        {/* Scrollable Room List */}
        <div className="overflow-auto" style={{ maxHeight: "214px" }}>
          {formik.values.rooms.Room.map((room, roomIndex) => (
            <div key={roomIndex} className="room-container mb-3">
              <h4>Room {roomIndex + 1}</h4>

              <div className="">
                <div className="d-flex  align-items-center justify-content-between">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Adults
                    </label>
                    <div className="d-flex align-items-center gap-2">
                      <button
                        className="w-5 h-5 rounded-full flex items-center justify-center border"
                        onClick={() => handleDecrement("adults", roomIndex)}
                        disabled={room.NumberOfAdults === 1}
                      >
                        -
                      </button>
                      <span>{room.NumberOfAdults}</span>
                      <button
                        className="w-5 h-5 rounded-full flex items-center justify-center border"
                        onClick={() => handleIncrement("adults", roomIndex)}
                        disabled={
                          room.NumberOfAdults + room.Child.NumberOfChild >= 12
                        }
                      >
                        +
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Children
                    </label>
                    <div className="d-flex align-items-center gap-2">
                      <button
                        className="w-5 h-5 rounded-full flex items-center justify-center border"
                        onClick={() => handleDecrement("children", roomIndex)}
                        disabled={room.Child.NumberOfChild === 0}
                      >
                        -
                      </button>
                      <span>{room.Child.NumberOfChild}</span>
                      <button
                        className="w-5 h-5 rounded-full flex items-center justify-center border"
                        onClick={() => handleIncrement("children", roomIndex)}
                        disabled={
                          room.NumberOfAdults + room.Child.NumberOfChild >= 12
                        }
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Buttons Fixed at Bottom */}
        <div
          className="d-flex align-items-center justify-content-between py-3"
          style={{ position: "sticky", bottom: "0", left: "0", width: "100%" }}
        >
          <button
            className=" add-room"
            onClick={handleAddRoom}
            disabled={formik.values.rooms.Room.length >= 12}
          >
            Add
          </button>
          {formik.values.rooms.Room.length > 1 && (
            <button
              className="remove-room"
              onClick={() =>
                handleRemoveRoom(formik.values.rooms.Room.length - 1)
              }
            >
              Remove
            </button>
          )}
        </div>
        <button className="done-btn" onClick={handleDone}>
          Done
        </button>
      </div>
    );
  };

  return (
    <div className="grow">
      <SideMenu />
      <div className="wrapper flex grow flex-col">
        <Header />
        <main className="grow content" role="content">
          <div className="container-fixed">
            <div className="card card-custom hotel-card">
              <div className="card-body">
                <div className="card-title full-width-underline">
                  Hotel Search
                </div>
                <div className="search-box">
                  <div className="p--1 pb-0">
                    <div className="row border-row">
                      <div className="col-md-3">
                        <label>City, Property name or Location</label>
                        <MuiSearchSelect
                          options={city}
                          placeholder="Select City"
                          isRequired
                          isInvalid={
                            formik.touched.city && !!formik.errors.city
                          }
                          errorText={formik.errors.city as string}
                          value={formik.values.city}
                          inputValue={citySearch}
                          onInputChange={(val) => setCitySearch(val)}
                          onChange={(value, id, selectedOption) => {
                            formik.setFieldValue("city", selectedOption?.label);
                            setCitySearch(selectedOption?.label || "");
                          }}
                          iconStart={<LocationOnIcon />}
                        />
                      </div>

                      <div className="col-md-3 ">
                        <label>Check In</label>
                        <MuiDatePicker
                          value={
                            formik.values.CheckInDate
                              ? dayjs(formik.values.CheckInDate)
                              : null
                          }
                          onChange={(date: string) => {
                            formik.setFieldValue("CheckInDate", date);

                            const checkIn = dayjs(date);
                            const checkOut = dayjs(formik.values.CheckOutDate);

                            if (!checkOut.isAfter(checkIn)) {
                              const newCheckout = checkIn
                                .add(1, "day")
                                .format("YYYY-MM-DD");
                              formik.setFieldValue("CheckOutDate", newCheckout);
                              formik.setFieldValue("nights", 1);
                            } else {
                              const nights = checkOut.diff(checkIn, "day");
                              formik.setFieldValue("nights", nights);
                            }
                          }}
                          errorText={formik.errors.CheckInDate}
                        />
                      </div>

                      <div className="col-md-3 ">
                        <label>Check Out</label>

                        <MuiDatePicker
                          value={
                            formik.values.CheckOutDate
                              ? dayjs(formik.values.CheckOutDate)
                              : null
                          }
                          onChange={(date: string) => {
                            const checkOut = dayjs(date);
                            const checkIn = dayjs(formik.values.CheckInDate);

                            if (checkOut.isAfter(checkIn)) {
                              formik.setFieldValue("CheckOutDate", date);
                              const nights = checkOut.diff(checkIn, "day");
                              formik.setFieldValue("nights", nights);
                              formik.setFieldError("CheckOutDate", "");
                            } else {
                              formik.setFieldError(
                                "CheckOutDate",
                                "Check-out must be after check-in date"
                              );
                            }
                          }}
                        />
                      </div>
                      <div className="col-md-3">
                        <label>Nights</label>
                        <MuiInput
                          type="number"
                          placeholder="Enter nights"
                          value={formik.values.nights.toString()}
                          onChange={(val: string) => {
                            const nights = parseInt(val) || 1;
                            formik.setFieldValue("nights", nights);

                            if (formik.values.CheckInDate) {
                              const newCheckout = dayjs(
                                formik.values.CheckInDate
                              )
                                .add(nights, "day")
                                .format("YYYY-MM-DD");
                              formik.setFieldValue("CheckOutDate", newCheckout);
                            }
                          }}
                        />
                      </div>
                      <div className="col-md-3 mt-2 position-relative">
                        <label>Rooms & Guests</label>
                        <MuiInput
                          placeholder="Rooms & Guests"
                          value={formik.values.rooms.Count || ""}
                          onClick={() => setIsPopupOpen(true)}
                          onChange={() => {}}
                        />

                        {isPopupOpen && (
                          <div className="popup-container position-absolute z-10 rounded p-3 mt-1 bg-info shadow">
                            {SelectTravellers()}
                          </div>
                        )}
                      </div>
                      {/* 
                      <div className="col-md-3 mt-2 ">
                        <label>Customer Name</label>
                        <MuiSearchSelect
                          options={corporateList}
                          placeholder="Select Customer Name"
                          isRequired
                          isInvalid={
                            formik.touched.corporateName &&
                            !!formik.errors.corporateName
                          }
                          errorText={formik.errors.corporateName as string}
                          onChange={(value) =>
                            formik.setFieldValue("corporateName", value)
                          }
                          value={formik.values.corporateName}
                        />
                      </div> */}

                      {/* <div className="col-md-3 mt-2">
                        <label>Nationality</label>
                        <MuiSearchSelect
                          placeholder="Select Nationality"
                          options={nationality}
                          isRequired
                          isInvalid={
                            formik.touched.nationality &&
                            !!formik.errors.nationality
                          }
                          errorText={formik.errors.nationality as string}
                          onChange={(value) =>
                            formik.setFieldValue("nationality", value)
                          }
                          value={formik.values.nationality}
                        />
                      </div> */}
                      {/* <div className="col-md-3 mt-2">
                        <label>Resident Country</label>
                        <MuiSearchSelect
                          placeholder="Select Resident Country"
                          options={nationality}
                          isRequired
                          isInvalid={
                            formik.touched.nationality &&
                            !!formik.errors.nationality
                          }
                          errorText={formik.errors.nationality as string}
                          onChange={(value) =>
                            formik.setFieldValue("nationality", value)
                          }
                          value={formik.values.nationality}
                        />
                      </div> */}
                      <div className="row mt-2">
                        <div className="col-md-6 mt-2">
                          <div className="d-flex justify-content-start align-items-center ">
                            <button
                              type="button"
                              aria-label="Advanced Filtration"
                              className="btn btn-outline-secondary fw-medium text-dark d-flex align-items-center gap-2"
                              onClick={handleRatingClick}
                              style={{
                                height: "38px",
                                minWidth: "180px",
                                backgroundColor: "#fff",
                                borderColor: "#ced4da",
                                border: "1px solid ",
                              }}
                            >
                              <span role="img" aria-label="search">
                                🔍
                              </span>
                              {selectedStars.length > 0
                                ? `${selectedStars.sort().join(", ")}★`
                                : "Advanced Filter"}
                            </button>
                          </div>

                          <Popper
                            style={{ zIndex: "9999" }}
                            open={isRatingPopperOpen}
                            anchorEl={ratingAnchorEl}
                            transition
                            placement="bottom-start"
                            disablePortal
                          >
                            {({ TransitionProps }) => (
                              <Fade {...TransitionProps} timeout={300}>
                                <Box
                                  sx={{
                                    border: 1,

                                    p: 2,
                                    bgcolor: "background.paper",
                                    borderRadius: 2,
                                    boxShadow: 3,
                                    width: "100%",
                                    maxWidth: "100vw",
                                    marginTop: "5px",
                                  }}
                                  className="container-fluid advance-fliter-card"
                                >
                                  <div className="row">
                                    <div className="col-12">
                                      <label className="fw-semibold d-block mb-2 mx-2">
                                        Star Rating
                                      </label>
                                      <div className="d-flex gap-2 flex-wrap">
                                        {[1, 2, 3, 4, 5].map((star) => (
                                          <button
                                            key={star}
                                            className={`btn btn-sm ${
                                              selectedStars.includes(star)
                                                ? "btn-primary"
                                                : "btn-outline-primary"
                                            }`}
                                            onClick={() =>
                                              toggleStarSelection(star)
                                            }
                                            style={{ minWidth: "50px" }}
                                          >
                                            {star}★
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                </Box>
                              </Fade>
                            )}
                          </Popper>
                        </div>

                        <div className="col-md-6 mt-2 ">
                          <div className="user-button d-flex justify-content-end align-items-center">
                            <MuiButton
                              className="get-btn mt-2 hotel-search-btn"
                              label="Search Hotel"
                              onClick={formik.submitForm}
                              type="button"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* <div className="mt-1">
                <ExclusiveOffersPreview />
              </div> */}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default HotelBooking;
